import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import {
  WEEKLY_SWITCH_COST,
  billingStore,
} from "../billing/billingStore";
import { chromePlanFromBilling, tierOf } from "./chromePlan";
import { flameWeekStore } from "./flameWeekStore";
import { heartWeekStore } from "./heartWeekStore";
import { commentStore, promptText, type CommentAnchor } from "./commentStore";
import {
  COMMENT_MAX_LETTERS,
  countLetters,
  HEARTS_PER_WEEK_GUEST,
  flamesPerWeek,
} from "./weights";
import { FlameSend } from "./FlameSend";
import { MatchCelebration } from "./MatchCelebration";
import { PackReveal } from "./PackReveal";
import { MatchCard } from "./MatchCard";
import { WeekFifaCard } from "./WeekFifaCard";
import { pairAlignmentScore } from "./engine";
import { matchStore } from "./matchStore";
import { interestStore } from "./interestStore";
import type { MatchProfile, MatchRecord } from "./types";
import { canUseLocalStorage } from "../storage/localWrite";
import { useLocale, useT, type Vars } from "../i18n";

/** Remember which ISO week already played the reveal — swaps force a replay. */
const PACK_WEEK_KEY = "un8.match.packWeek.v1";

/** Survives MatchApp `key={tick}` remounts mid-animation. */
let packRevealActive = false;

type FlameHold = {
  profile: MatchProfile;
  comment?: { anchorId: CommentAnchor; text: string };
};

/** Flame overlay + aftermath must live here — `like()` remounts MatchFeed. */
let flameHold: FlameHold | null = null;
let flameAnimDone = false;
/** Survives MatchApp remounts until the member dismisses the celebration. */
let heldMatch: { profile: MatchProfile; record: MatchRecord } | null = null;
let pendingFlameNote: string | null = null;
const flameListeners = new Set<() => void>();
function emitFlame() {
  for (const fn of flameListeners) fn();
}
function subscribeFlame(fn: () => void) {
  flameListeners.add(fn);
  return () => {
    flameListeners.delete(fn);
  };
}

function translateDeckReason(raw: string | null, t: (key: string, vars?: Vars) => string): string | null {
  if (!raw) return null;
  if (/sign in required|sign in to use matching|sign in first|unauthorized/i.test(raw)) {
    return t("billing.signInToMatch");
  }
  if (/waiting for moderation approval/i.test(raw)) {
    return t("match.waitingModeration");
  }
  if (/Complete and submit your profile/i.test(raw)) {
    return t("match.completeProfileFirst");
  }
  if (/subscribe|see new people|free trial ended|keep chatting|keep everyone|who liked|extra reveal|credits/i.test(raw)) {
    return t("billing.paywallCopy");
  }
  return raw;
}

function translateSwapError(raw: string, t: (key: string, vars?: Vars) => string): string {
  if (/not enough credits/i.test(raw)) return t("match.swapNoCredits");
  if (/sign in/i.test(raw)) return t("billing.signInToMatch");
  return raw;
}

function readPackWeek(): string | null {
  try {
    return localStorage.getItem(PACK_WEEK_KEY);
  } catch {
    return null;
  }
}

function writePackWeek(weekKey: string) {
  try {
    localStorage.setItem(PACK_WEEK_KEY, weekKey);
  } catch {
    /* ignore */
  }
}

/** Leaving Selección must not leave the pack painted under Mensajes / Perfil. */
export function dismissPackReveal() {
  packRevealActive = false;
  try {
    const weekKey = matchStore.getWeeklyMeta()?.weekKey;
    if (weekKey) writePackWeek(weekKey);
  } catch {
    /* ignore */
  }
}

export function MatchFeed({
  onOpenChat,
}: {
  onOpenChat: (matchId: string) => void;
}) {
  const t = useT();
  const { locale } = useLocale();
  const [tick, setTick] = useState(0);
  const [match, setMatch] = useState<{ profile: MatchProfile; record: MatchRecord } | null>(
    () => heldMatch
  );
  const [revealNote, setRevealNote] = useState<string | null>(null);
  const [swapNote, setSwapNote] = useState<string | null>(null);
  const [likeNote, setLikeNote] = useState<string | null>(() => pendingFlameNote);
  const [swapAnim, setSwapAnim] = useState(false);
  const [packOpen, setPackOpen] = useState(() => packRevealActive);
  const [packTitlePulse, setPackTitlePulse] = useState(() => packRevealActive);
  /** Comment composer — null when closed. */
  const [composeFor, setComposeFor] = useState<MatchProfile | null>(null);
  const [composeKind, setComposeKind] = useState<"heart" | "flame">("heart");
  const [composeAnchor, setComposeAnchor] = useState<CommentAnchor>("about");
  const [composeText, setComposeText] = useState("");
  const [packMode, setPackMode] = useState<"deal" | "refresh">("deal");
  const [flippedId, setFlippedId] = useState<string | null>(null);
  const [readingProfile, setReadingProfile] = useState<MatchProfile | null>(null);
  const [showScoreDetail, setShowScoreDetail] = useState(false);
  const composeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => matchStore.subscribe(() => setTick((n) => n + 1)), []);
  useEffect(() => billingStore.subscribe(() => setTick((n) => n + 1)), []);
  useEffect(() => subscribeFlame(() => setTick((n) => n + 1)), []);
  useEffect(() => {
    if (heldMatch) {
      setMatch(heldMatch);
    }
    if (pendingFlameNote) {
      setLikeNote(pendingFlameNote);
    }
  }, [tick]);
  useEffect(() => {
    if (!composeFor || !composeRef.current) return;
    composeRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
    composeRef.current.querySelector("input")?.focus();
  }, [composeFor]);

  const state = matchStore.getState();
  const self = state.self;
  const queue = matchStore.getDailyQueue();
  const weekly = matchStore.getWeeklyMeta();
  const poolSource = matchStore.usingDemoFallback()
    ? "demo"
    : matchStore.poolSource();
  const liveCount = matchStore.poolSize();
  const matchCount = matchStore.listMatches().length;
  const likedCount = state.likedIds.length;
  const isGuest = Boolean(self?.id.startsWith("user_guest_"));
  const rawDeckReason = isGuest ? null : matchStore.serverDeckReason();
  const approved =
    isGuest || matchStore.effectiveReviewStatus() === "approved";
  const deckReason =
    approved &&
    rawDeckReason &&
    (/moderat|approval|waiting for|sign in required|sign in to use|sign in first|unauthorized/i.test(
      rawDeckReason
    ))
      ? null
      : translateDeckReason(rawDeckReason, t);
  const demoWhileWaiting = Boolean(queue.length && deckReason && matchStore.usingDemoFallback());
  const billing = billingStore.get();
  const chromePlan = chromePlanFromBilling(billing);
  /** Guest demo: 2 hearts / 1 flame per week (Kirk 2026-09-12). */
  const heartBudget: number | null = isGuest
    ? HEARTS_PER_WEEK_GUEST
    : billing.heartBudget;
  const heartsLeft: number | null = self
    ? heartWeekStore.remaining(self.id, heartBudget)
    : 0;
  const outOfHearts = heartsLeft !== null && heartsLeft <= 0;
  const flameBudget: number | null = isGuest ? 1 : flamesPerWeek(billing.plan);
  const flamesLeft: number | null = self
    ? flameWeekStore.remaining(self.id, flameBudget)
    : 0;
  const outOfFlames = flamesLeft !== null && flamesLeft <= 0;

  const canAffordSwap =
    billing.unlimitedCredits ||
    billing.canUnlimitedSwap ||
    (billing.credits != null && billing.credits >= WEEKLY_SWITCH_COST);
  /** Show Refresh control for any tier that can swap — disable when broke. */
  const showSwapControl =
    isGuest ||
    billing.canUnlimitedSwap ||
    billing.canDailySwap ||
    billing.plan === "free" ||
    billing.plan === "trial";

  const openPack = useCallback((mode: "deal" | "refresh" = "deal") => {
    packRevealActive = true;
    setPackMode(mode);
    setPackOpen(true);
    setPackTitlePulse(mode === "refresh");
  }, []);

  const completePack = useCallback(() => {
    packRevealActive = false;
    writePackWeek(weekly.weekKey);
    setPackOpen(false);
    setPackTitlePulse(false);
    setSwapAnim(false);
  }, [weekly.weekKey]);

  // First reveal of this week's four (not on pass-replace). Swaps call openPack().
  useEffect(() => {
    if (queue.length === 0) return;
    if (packRevealActive) {
      if (!packOpen) setPackOpen(true);
      return;
    }
    if (readPackWeek() !== weekly.weekKey) openPack();
  }, [weekly.weekKey, queue.length, packOpen, openPack]);

  /**
   * Send a heart with a required short note (COMMENT_MAX_LETTERS).
   *
   * The heart is spent BEFORE the like is recorded. If the budget is gone the
   * like never happens — a scarcity that can be bypassed by the UI is not a
   * scarcity. Re-hearting someone already hearted is free (store handles it).
   */
  const like = useCallback(
    (
      profile: MatchProfile,
      comment?: { anchorId: CommentAnchor; text: string },
      kind: "heart" | "flame" = "heart"
    ) => {
      setLikeNote(null);
      const viewerId = matchStore.getState().self?.id;
      if (!viewerId) return;
      if (!comment || !commentStore.isValid(comment.text)) {
        setLikeNote(
          t("match.commentTooLong", { max: COMMENT_MAX_LETTERS })
        );
        window.setTimeout(() => setLikeNote(null), 3200);
        return;
      }

      if (kind === "flame") {
        const budget = viewerId.startsWith("user_guest_")
          ? 1
          : flamesPerWeek(billingStore.get().plan);
        const spent = flameWeekStore.spend(viewerId, profile.id, budget);
        if (!spent.ok) {
          setLikeNote(t("match.flameNoneLeft"));
          window.setTimeout(() => setLikeNote(null), 4200);
          return;
        }
        flameHold = { profile, comment };
        flameAnimDone = false;
        heldMatch = null;
        pendingFlameNote = null;
        setComposeFor(null);
        setComposeText("");
        emitFlame();
        setTick((n) => n + 1);
        return;
      } else {
        const budget = viewerId.startsWith("user_guest_")
          ? HEARTS_PER_WEEK_GUEST
          : billingStore.get().heartBudget;
        const spent = heartWeekStore.spend(viewerId, profile.id, budget);
        if (!spent.ok) {
          setLikeNote(t("match.heartsNoneLeft"));
          window.setTimeout(() => setLikeNote(null), 4200);
          return;
        }
      }

      // Comment rides with the heart. A rejection here must not silently
      // swallow the heart, so the like still goes through and the note says so.
      let commentFailed = false;
      if (comment) {
        const res = commentStore.send({
          fromUserId: viewerId,
          toProfileId: profile.id,
          anchorId: comment.anchorId,
          text: comment.text,
        });
        if (!res.ok && res.error !== "already_sent") commentFailed = true;
      }

      setComposeFor(null);
      setComposeText("");

      void matchStore.like(profile.id).then((record) => {
        if (record) {
          heldMatch = { profile, record };
        } else {
          pendingFlameNote = commentFailed
            ? t("match.commentTooLong", { max: COMMENT_MAX_LETTERS })
            : t("match.likeSent", { name: profile.firstName });
          window.setTimeout(() => {
            pendingFlameNote = null;
            setLikeNote(null);
          }, 3200);
        }
        emitFlame();
      });
    },
    [t]
  );

  const finishFlame = useCallback(() => {
    if (!flameHold || flameAnimDone) return;
    flameAnimDone = true;
    const hold = flameHold;
    const viewerId = matchStore.getState().self?.id;
    let commentFailed = false;
    if (hold.comment && viewerId) {
      const res = commentStore.send({
        fromUserId: viewerId,
        toProfileId: hold.profile.id,
        anchorId: hold.comment.anchorId,
        text: hold.comment.text,
      });
      if (!res.ok && res.error !== "already_sent") commentFailed = true;
    }
    void matchStore.like(hold.profile.id).then((record) => {
      if (record) {
        heldMatch = { profile: hold.profile, record };
        pendingFlameNote = null;
      } else {
        heldMatch = null;
        pendingFlameNote = commentFailed
          ? t("match.commentTooLong", { max: COMMENT_MAX_LETTERS })
          : t("match.flameSent", { name: hold.profile.firstName });
        window.setTimeout(() => {
          pendingFlameNote = null;
          setLikeNote(null);
        }, 3200);
      }
      flameHold = null;
      emitFlame();
    });
  }, [t]);

  const openCompose = useCallback((
    profile: MatchProfile,
    anchor: CommentAnchor = "about",
    kind: "heart" | "flame" = "heart"
  ) => {
    setComposeKind(kind);
    setComposeFor(profile);
    setComposeAnchor(anchor);
    setComposeText("");
  }, []);

  const openCardForHeart = useCallback(
    (profile: MatchProfile, kind: "heart" | "flame" = "heart") => {
      const alreadyLiked = matchStore.getState().likedIds.includes(profile.id);
      if (alreadyLiked && kind === "heart") {
        setLikeNote(t("match.likedStays"));
        window.setTimeout(() => setLikeNote(null), 3200);
        return;
      }
      openCompose(profile, "photo", kind);
    },
    [openCompose, t]
  );

  const spendReveal = () => {
    setRevealNote(null);
    const res = matchStore.spendExtraReveal();
    if (!res.ok) setRevealNote(res.error);
  };

  const swapFour = () => {
    setSwapNote(null);
    if (!canAffordSwap) {
      setSwapNote(t("match.swapNoCredits"));
      return;
    }
    setSwapAnim(true);
    const res = matchStore.swapWeeklyFour();
    if (!res.ok) {
      setSwapNote(translateSwapError(res.error, t));
      window.setTimeout(() => setSwapAnim(false), 1400);
      return;
    }
    setComposeFor(null);
    setFlippedId(null);
    setReadingProfile(null);
    openPack("refresh");
    window.setTimeout(() => setSwapAnim(false), 1400);
  };

  if (!self) {
    return (
      <div className="match-empty">
        <h1>{t("match.noProfileYet")}</h1>
        <p>{t("match.noProfileText")}</p>
        <p>
          <a className="match-nav__link" href="#/">
            {t("match.goOnboarding")}
          </a>
        </p>
      </div>
    );
  }

  if (flameHold) {
    return (
      <FlameSend
        profile={flameHold.profile}
        onComplete={finishFlame}
        settled={flameAnimDone}
      />
    );
  }

  if (heldMatch || match) {
    const hit = heldMatch ?? match;
    return (
      <MatchCelebration
        self={self}
        other={hit!.profile}
        record={hit!.record}
        onContinue={() => {
          heldMatch = null;
          setMatch(null);
          emitFlame();
        }}
        onOpenChat={onOpenChat}
      />
    );
  }

  const storageDown =
    !canUseLocalStorage() || matchStore.sessionWriteFailed() || interestStore.writeFailed();

  const countdown =
    weekly.daysUntilNext <= 0
      ? t("match.weeklyCountdownSoon")
      : weekly.daysUntilNext === 1
        ? t("match.weeklyCountdownOne")
        : t("match.weeklyCountdown", { days: weekly.daysUntilNext });
  void locale;

  const title =
    packTitlePulse && packOpen
      ? t("match.newSelection")
      : weekly.presentedCount === 4
        ? t("match.weeklyTitle")
        : weekly.presentedCount > 0
          ? t("match.weeklyTitleCount", { count: weekly.presentedCount })
          : t("match.weeklyTitle");

  const shortCopy =
    weekly.thinPool && weekly.presentedCount === 2
      ? t("match.shortWeekTwo")
      : weekly.thinPool && weekly.presentedCount > 0
        ? t("match.shortWeekCopy", { count: weekly.presentedCount })
        : null;

  const swapButton = showSwapControl ? (
    <div className="match-empty__actions">
      <Button onClick={swapFour} disabled={!canAffordSwap} aria-disabled={!canAffordSwap}>
        {t("match.swapFour")}
      </Button>
      {!canAffordSwap ? (
        <p className="match-empty__meta" role="status">
          {t("match.swapNoCredits")}
        </p>
      ) : null}
      {swapNote ? <p className="match-empty__meta">{swapNote}</p> : null}
    </div>
  ) : null;

  if (queue.length === 0) {
    const neverHadDeck = weekly.presentedCount === 0;
    return (
      <div className="match-empty screen-enter" data-viewer-plan={chromePlan} key={tick}>
        <p className="match-feed__countdown" lang={locale} data-i18n="match.weeklyCountdown">
          {countdown}
        </p>
        <h1>
          {deckReason && !demoWhileWaiting
            ? t("match.matchingPaused")
            : neverHadDeck
              ? t("match.noOneYet")
              : t("match.weekDone")}
        </h1>
        <p>
          {deckReason && !demoWhileWaiting
            ? deckReason
            : neverHadDeck
              ? t("match.emptyNeverHad")
              : t("match.weekDoneText")}
        </p>
        {shortCopy ? <p className="match-empty__meta">{shortCopy}</p> : null}
        {storageDown ? (
          <p className="match-empty__meta" role="alert">
            {canUseLocalStorage() ? t("common.storageQuota") : t("common.storageDisabled")}
            {t("match.likesMayNotStick")}
          </p>
        ) : (
          <p className="match-empty__meta">
            {t("match.poolMeta", {
              pool:
                poolSource === "demo"
                  ? t("match.demoPool")
                  : t("match.livePool", { source: poolSource, count: liveCount }),
              matches: matchCount,
              liked: likedCount,
            })}
          </p>
        )}
        {swapButton}
        {billing.canUseExtraReveals ? (
          <div className="match-empty__actions">
            <Button onClick={spendReveal}>
              {t("match.extraReveal")} ({weekly.extraRevealsRemaining})
            </Button>
            {revealNote ? <p className="match-empty__meta">{revealNote}</p> : null}
          </div>
        ) : null}
        <div className="match-empty__actions">
          <Button
            variant="ghost"
            onClick={() => {
              try {
                localStorage.removeItem(PACK_WEEK_KEY);
              } catch {
                /* ignore */
              }
              matchStore.resetDemo();
              openPack("refresh");
              setTick((n) => n + 1);
            }}
          >
            {t("match.resetDeck")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`match-feed match-feed--weekly${swapAnim ? " is-swapping" : ""}${packOpen ? " is-pack-open" : ""}`}
      data-viewer-plan={chromePlan}
    >
      {readingProfile ? (
        <div className="match-profile-reader" role="dialog" aria-modal="true" aria-label={readingProfile.firstName}>
          <div className="match-profile-reader__bar">
            <button
              type="button"
              onClick={() => setReadingProfile(null)}
            >
              {t("match.backToFour")}
            </button>
            {(() => {
              const mutualRecord = state.matches.find((m) => m.profileId === readingProfile.id);
              if (!mutualRecord) {
                return (
                  <p className="match-profile-reader__wait" role="status">
                    {t("match.waitLikeBack", { name: readingProfile.firstName })}
                  </p>
                );
              }
              return (
                <button
                  type="button"
                  className="match-profile-reader__chat"
                  onClick={() => {
                    matchStore.unlockChat(mutualRecord.id);
                    setReadingProfile(null);
                    onOpenChat(mutualRecord.id);
                  }}
                >
                  {t("match.openChat")}
                </button>
              );
            })()}
          </div>
          <div className="match-profile-reader__body">
            <MatchCard
              profile={readingProfile}
              selfScales={self.scales}
              selfAnswers={self.answers}
              theyLikedYou={matchStore.theyLikedYou(readingProfile.id)}
              showScoreDetail={showScoreDetail}
              onToggleScore={() => setShowScoreDetail((v) => !v)}
              offsetX={0}
              dragging={false}
              fullBleed
            />
          </div>
        </div>
      ) : null}
      {demoWhileWaiting ? (
        <p className="match-empty__meta" role="status">
          {deckReason} {t("match.moderationDemoNote")}
        </p>
      ) : null}
      {storageDown ? (
        <p className="match-empty__meta" role="alert">
          {canUseLocalStorage() ? t("common.storageQuota") : t("common.storageDisabled")}
          {t("match.likesMayNotStick")}
        </p>
      ) : null}
      {likeNote ?? pendingFlameNote ? (
        <p className="match-feed__like-note" role="status">
          {likeNote ?? pendingFlameNote}
        </p>
      ) : null}

      <header className="match-feed__head">
        <div>
          <p className="match-feed__eyebrow">
            {poolSource === "demo"
              ? t("match.feedEyebrowDemo")
              : t("match.feedEyebrowLive", { source: poolSource, count: liveCount })}
          </p>
          <h1 className="match-feed__title">{title}</h1>
          <p className="match-feed__countdown" lang={locale} data-i18n="match.weeklyCountdown">
            {countdown}
          </p>
          {shortCopy ? <p className="match-feed__short">{shortCopy}</p> : null}
        </div>
        <span className="match-feed__count">{t("match.left", { count: queue.length })}</span>
      </header>

      {composeFor && !packOpen && !(composeKind === "heart" && outOfHearts && !state.likedIds.includes(composeFor.id)) && !(composeKind === "flame" && outOfFlames) ? (
            <div ref={composeRef} className="match-compose match-compose--sheet">
              <p className="match-compose__lead">
                {composeKind === "flame"
                  ? t("match.flameLead", { name: composeFor.firstName })
                  : t("match.commentLead", { name: composeFor.firstName })}
              </p>
              <div className="match-compose__quote">
                {composeAnchor === "photo" && composeFor.photoUrl ? (
                  <img
                    src={composeFor.photoUrl}
                    alt=""
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <blockquote>
                    {promptText(composeFor, composeAnchor) || composeFor.firstName}
                  </blockquote>
                )}
              </div>
              <input
                className="match-compose__input"
                type="text"
                value={composeText}
                maxLength={COMMENT_MAX_LETTERS}
                placeholder={t("match.commentPlaceholder")}
                aria-label={t("match.commentLead", { name: composeFor.firstName })}
                onChange={(e) => setComposeText(e.target.value)}
              />
              <p
                className={`match-compose__count${
                  countLetters(composeText) > COMMENT_MAX_LETTERS ? " is-over" : ""
                }${
                  countLetters(composeText) === COMMENT_MAX_LETTERS - 1 ? " is-near" : ""
                }`}
                role="status"
              >
                {t("match.commentCount", {
                  count: countLetters(composeText),
                  max: COMMENT_MAX_LETTERS,
                })}
              </p>
              <div className="match-compose__actions">
                <Button
                  onClick={() =>
                    like(composeFor, { anchorId: composeAnchor, text: composeText }, composeKind)
                  }
                  disabled={!commentStore.isValid(composeText)}
                  aria-disabled={!commentStore.isValid(composeText)}
                >
                  {composeKind === "flame" ? t("match.flameSend") : t("match.commentSend")}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setComposeFor(null);
                    setComposeText("");
                  }}
                >
                  {t("match.composeCancel")}
                </Button>
              </div>
            </div>
          ) : null}

      <div className="match-week-stage">
          {packOpen ? (
            <PackReveal
              profiles={queue}
              self={self}
              viewerPlan={chromePlan}
              mode={packMode}
              onSettle={() => writePackWeek(weekly.weekKey)}
              onComplete={completePack}
            />
          ) : null}
          <ul
            className="match-week-grid match-week-grid--bleed"
            data-viewer-plan={chromePlan}
            aria-hidden={packOpen}
          >
            {queue.map((p, i) => {
              const tileScore = pairAlignmentScore(self, p);
              const revealed = state.likedIds.includes(p.id);
              const mutualRecord = state.matches.find((m) => m.profileId === p.id) ?? null;
              const mutual = Boolean(mutualRecord);
              return (
              <li key={p.id} style={{ animationDelay: `${i * 140}ms` }}>
                <div
                  className={`match-week-tile-wrap${flippedId === p.id ? " is-flipped" : ""}`}
                  data-plan={tierOf(p)}
                >
                  <WeekFifaCard
                    profile={p}
                    self={self}
                    score={tileScore}
                    flipped={flippedId === p.id}
                    revealed={revealed}
                    mutual={mutual}
                    disabled={packOpen}
                    onPeek={(open) => {
                      if (open) setFlippedId(p.id);
                      else setFlippedId((cur) => (cur === p.id ? null : cur));
                    }}
                    onGoToProfile={() => {
                      if (!revealed) return;
                      setFlippedId(null);
                      setShowScoreDetail(false);
                      setReadingProfile(p);
                    }}
                  />
                  <div className="match-week-tile__acts">
                    <button
                      type="button"
                      className={`match-week-tile__heart${revealed ? " is-sent" : ""}`}
                      disabled={packOpen || (outOfHearts && !revealed)}
                      aria-label={revealed ? t("match.likedStays") : t("match.likeHeart")}
                      title={
                        revealed
                          ? t("match.likedStays")
                          : outOfHearts
                            ? t("match.heartsNoneLeft")
                            : t("match.commentLead", { name: p.firstName })
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (outOfHearts && !revealed) return;
                        openCardForHeart(p, "heart");
                      }}
                    >
                      ♥
                    </button>
                    <button
                      type="button"
                      className="match-week-tile__flame"
                      disabled={packOpen || outOfFlames}
                      aria-label={t("match.flame")}
                      title={outOfFlames ? t("match.flameNoneLeft") : t("match.flameLead", { name: p.firstName })}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (outOfFlames) return;
                        openCardForHeart(p, "flame");
                      }}
                    >
                      🔥
                    </button>
                  </div>
                </div>
              </li>
            );
            })}
          </ul>
        </div>

      {outOfHearts && !packOpen ? (
        <p className="match-feed__hint" role="status">
          {t("match.heartsNoneLeft")}
        </p>
      ) : !packOpen ? (
        <p className="match-feed__hint">{t("match.swipeHint")}</p>
      ) : null}

      {showSwapControl && !packOpen ? (
        <div className="match-extra">
          <Button
            variant="ghost"
            onClick={swapFour}
            disabled={!canAffordSwap}
            aria-disabled={!canAffordSwap}
          >
            {t("match.swapFour")}
          </Button>
          {!canAffordSwap ? (
            <p className="match-empty__meta" role="status">
              {t("match.swapNoCredits")}
            </p>
          ) : null}
          {swapNote ? <p className="match-empty__meta">{swapNote}</p> : null}
        </div>
      ) : null}

      {billing.canUseExtraReveals && weekly.extraRevealsRemaining > 0 && !packOpen ? (
        <div className="match-extra">
          <Button variant="ghost" onClick={spendReveal}>
            {t("match.extraReveal")}
          </Button>
          <p className="match-empty__meta">
            {t("match.extraRevealLeft", { count: weekly.extraRevealsRemaining })}
          </p>
          {revealNote ? <p className="match-empty__meta">{revealNote}</p> : null}
        </div>
      ) : null}

    </div>
  );
}
